import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Button,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Stack
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { ChatMessage, ChatSession } from '../../services/geminiService';

interface AIChatInterfaceProps {
  isOpen: boolean;
  onClose: () => void;
  session: ChatSession | null;
  onSendMessage: (message: string) => Promise<ChatMessage | null>;
  onUpdateSession: (session: ChatSession) => void;
  isLoading: boolean;
  error: string | null;
}

const AIChatInterface: React.FC<AIChatInterfaceProps> = ({
  isOpen,
  onClose,
  session,
  onSendMessage,
  onUpdateSession,
  isLoading,
  error
}) => {
  const [message, setMessage] = useState('');
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Sincronizar mensajes locales con la sesión
  useEffect(() => {
    if (session?.messages) {
      setLocalMessages(session.messages);
    }
  }, [session?.messages]);

  // Limpiar mensajes locales cuando se cierra el chat
  useEffect(() => {
    if (!isOpen) {
      setLocalMessages([]);
    }
  }, [isOpen]);

  // Limpiar mensajes locales cuando se cierra el chat
  useEffect(() => {
    if (!isOpen) {
      setLocalMessages([]);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading || !session) return;
    
    const currentMessage = message.trim();
    setMessage('');
    
    // Agregar el mensaje del usuario inmediatamente a los mensajes locales
    const userMessage: ChatMessage = {
      role: 'user',
      content: currentMessage,
      timestamp: new Date()
    };
    
    // Actualizar mensajes locales inmediatamente
    setLocalMessages(prev => [...prev, userMessage]);
    
    // Llamar a la función del padre para obtener la respuesta del AI
    try {
      const aiMessage = await onSendMessage(currentMessage);
      if (aiMessage) {
        // Agregar la respuesta del AI a los mensajes locales
        setLocalMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };



  if (!isOpen) return null;

  return (
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1200,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(4px)'
      }}
    >
      <Box
        sx={{
          width: '600px',
          maxWidth: '90vw',
          height: '80vh',
          maxHeight: '600px',
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: '12px 12px 0 0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            DU-IV Document Chat
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          backgroundColor: '#f8f9fa',
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '3px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a8a8a8'
          }
        }}
      >
                {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!session && isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="h5" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                Welcome to DU-IV Chat
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Initializing intelligent document analysis...
              </Typography>
            </Box>
          </Box>
        )}

        {localMessages.map((msg, index) => (
          <Box
            key={`${msg.role}-${msg.timestamp.getTime()}-${index}`}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 2
            }}
          >
            <Box
              sx={{
                maxWidth: '80%',
                display: 'flex',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: 1
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main'
                }}
              >
                {msg.role === 'user' ? <PersonIcon /> : <AIIcon />}
              </Avatar>
              
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: msg.role === 'user' ? 'primary.main' : 'white',
                  color: msg.role === 'user' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  boxShadow: 1,
                  position: 'relative'
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
                
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1,
                    opacity: 0.7,
                    fontSize: '0.75rem'
                  }}
                >
                  {formatTime(msg.timestamp)}
                </Typography>
              </Paper>
            </Box>
          </Box>
        ))}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                <AIIcon />
              </Avatar>
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                  AI is thinking...
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 3,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'white',
          borderRadius: '0 0 12px 12px'
        }}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder={!session ? "Initializing..." : "Type your message..."}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading || !session}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!message.trim() || isLoading || !session}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500'
              }
            }}
          >
            <SendIcon />
          </IconButton>
        </Stack>
        
        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
          Press Enter to send, Shift+Enter for new line
        </Typography>
      </Box>
      </Box>
    </Box>
  );
};

export default AIChatInterface; 